import classNames from 'classnames'
import React, { MutableRefObject } from 'react'
import ReactDOMServer from "react-dom/server"
import { ContactMessageDetailsContextProvider } from '../src/contexts/ContactMessageDetailsContext'
import { useLocalization } from '../src/hooks/useLocaleFormatter'
import { StaircaseAssemblyConfiguratorLandingFragment as StaircaseAssemblyConfiguratorLandingFragment, StaircaseAssemblyConfiguratorSamplerFragment as StaircaseAssemblyConfiguratorSamplerFragment, StaircaseAssemblyConfiguratorStepFragment as StaircaseAssemblyConfiguratorStepFragment, StaircaseAssemblyConfiguratorStringerFragment as StaircaseAssemblyConfiguratorStringerFragment, StaircaseAssemblyConfiguratorSupportFragment as StaircaseAssemblyConfiguratorSupportFragment, UUID } from '../src/model/fragments'
import { getLocale, Locale } from '../src/model/locale'
import { multiplyPrices, sumPriceArrays } from '../src/utils/formatPrice'
import { numericInputChangeHandler } from '../src/utils/inputChangeHandler'
import { StaircaseAssemblyConfiguratorUtils } from '../src/utils/staircaseConfigurator'
import { Button } from './Button'
import { CardView } from './Card'
import { CardBox } from './CardBox'
import { ContactForm } from './ContactForm'
import { ExportButton } from './ExportButton'
import SvgQuestionMark from './icons/SvgQuestionMark'
import { Input } from './Input'
import { Picture, PictureEncode } from './Picture'
import { ShareLink } from './ShareLink'
import { Spacer } from './Spacer'

enum StaircaseAssemblyConfiguratorState {
	surface=1,
	step = 2,
	stringer = 3,
	landing = 4,
	support = 5,
	final = 6
};

type StaircaseAssemblyConfiguratorFormData = {
	sampler: StaircaseAssemblyConfiguratorSamplerFragment | null,

	step: StaircaseAssemblyConfiguratorStepFragment | null,
	stepLength: number | null,
	stepCount: number | null,

	stringer: StaircaseAssemblyConfiguratorStringerFragment | null,
	stringerHeight: number | null,
	stringerCount: number | null,

	landing: StaircaseAssemblyConfiguratorLandingFragment | null,
	landingLength: number | null,
	landingDepth: number | null,

	support: StaircaseAssemblyConfiguratorSupportFragment | null,
	supportCount: number | null
}

type StaircaseAssemblyConfiguratorDTO = Omit<StaircaseAssemblyConfiguratorFormData, 'sampler' | 'step' | 'stringer' | 'landing' | 'support'> & {
	samplerId: UUID,
	stepId: UUID,
	stringerId: UUID,
	landingId: UUID | null,
	supportId: UUID
};

type StaircaseAssemblyConfiguratorStateProps = {
	locales: Locale[],

	samplers: StaircaseAssemblyConfiguratorSamplerFragment[],
	steps: StaircaseAssemblyConfiguratorStepFragment[],
	stringers: StaircaseAssemblyConfiguratorStringerFragment[],
	landings: StaircaseAssemblyConfiguratorLandingFragment[],
	supports: StaircaseAssemblyConfiguratorSupportFragment[],

	state: StaircaseAssemblyConfiguratorState,
	setState: (s: StaircaseAssemblyConfiguratorState) => void,

	formData: StaircaseAssemblyConfiguratorFormData,
	setFormData: (d: StaircaseAssemblyConfiguratorFormData) => void
}

const createBlankFormData = () => ({
	sampler: null,

	step: null, 
	stepCount: null,
	stepLength: null,

	stringer: null,
	stringerHeight: null,
	stringerCount: null,

	landing: null,
	landingDepth: null,
	landingLength: null,

	support: null,
	supportCount: null
});


/**************************************************************** */
/** Krok 1 - vyber ciloveho provedeni     *********************** */
/**************************************************************** */

const StaircaseAssemblyConfiguratorSurfaceFinish : React.FunctionComponent<StaircaseAssemblyConfiguratorStateProps> = 
	({ samplers, locales, state, setState, formData, setFormData }) => {

	React.useEffect(() => {
		if(state === StaircaseAssemblyConfiguratorState.surface)
		{
			setFormData({...formData, sampler: null});
		}
	}, [state]);

	return (
		<>
			<div className={classNames('staircaseConfiguratorLayout-state', 'staircaseConfiguratorLayout-surfaceFinish')}>
				<header>
					<h2><span className="number">1</span> Povrchová úprava schodiště</h2>
					<p>Začněte prosím výběrem požadované povrchové úpravy pro vaše schodiště.</p>
					<Spacer initial={40} />

				</header>
				<div>
					<h3>Vyberte vzorník:</h3>
					<div className="grid--samplers">
					{
						samplers.map(ss => {
							
							const data = getLocale(ss.locales, locales);
							
							return data && <CardBox 
								isActive={ formData.sampler == ss }
								onClick={() => {
									setFormData({
										// FIXME: toto by melo nejak fungovat i bez toho abychom data cistili tady
										//...formData, 
										...createBlankFormData(),
										sampler: ss,
									})

									setState(StaircaseAssemblyConfiguratorState.step);
								}}
								title={''}
								categoryTag={''}
								view={CardView.default}
								content={
									<div className="flex">
										<div>
										<Picture
											image={ss.image}
											width={140}
											height={140}
											alt={data.name}
											mode="cover"
											encode={PictureEncode.jpg}
										/>
										</div>
										<div>
											<h3 dangerouslySetInnerHTML={{__html: data.name}}></h3>
											<p>{data.name}</p>
										</div>
									</div>
								}
							/>;

						})
					}
					</div>
				</div>
				<Spacer initial={60} />
			</div>
		</>
	);
};

/**************************************************************** */
/** Krok 2 - parametry schodistovych stupnu ********************* */
/**************************************************************** */

const StaircaseAssemblyConfiguratorStep : React.FunctionComponent<StaircaseAssemblyConfiguratorStateProps> = 
	({ locales, steps, formData, setFormData, state, setState }) => {
	const [inputStepLength, setInputStepLength] = React.useState<string>('');

	React.useEffect(() => {
		if(state === StaircaseAssemblyConfiguratorState.step)
		{
			setFormData({
				...formData,
				step: null,
				stepLength: null,
				stepCount: null
			});

			setInputStepLength('');
		}
	}, [state]);

	const localization = useLocalization();
	const ref = React.useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
	React.useEffect(() => {
		if(ref.current)
		{
			ref.current.scrollIntoView({
				behavior: 'smooth'
			});
		}
	}, [ref]);

	return (
		<>
			<div ref={ref} className={classNames('staircaseConfiguratorLayout-state', 'staircaseConfiguratorLayout-steps')}>
				<header>
					<h2><span className="number">2</span> Schodišťový stupeň</h2>
					<p>Níže vyberte požadovanou specifikaci schodišťového stupně výběrem hloubky a zadáním šířky. Tyto schodišťové stupně budou pro vás vyrobeny ve výše vybrané povrchové úpravě.</p>
					<Spacer initial={40} />

				</header>
				<div className="grid--image">	
					<div>
						<h3>Vyberte:</h3>
						<div className="grid--selection">
						{
							formData.sampler && steps.filter(st => st.samplerIds.includes(formData.sampler!.id)).map(st => {
								
								const data = getLocale(st.locales, locales);
								
								let prices = localization.extractPrices(st.prices);
								prices = StaircaseAssemblyConfiguratorUtils.applyAssemblyPriceMultipliers(prices);

								return data && <CardBox 
									isActive={ formData.step == st }
									onClick={() => {

										const len = formData.stepLength ? Math.max(st.minLength, Math.min(formData.stepLength, st.maxLength)) : null;
										setFormData({
											...formData,
											step: st,
											stepLength: len
										});

										if(len)
										{
											setInputStepLength(len.toString());
										}
									}}
									title={'Hloubka ' + st.depth + 'mm'}
									categoryTag={'Délka stupně: ' + st.minLength  + 'mm - ' + st.maxLength + 'mm'}
									view={CardView.default}
									content={
										<>
										
										<p>
											<small>Cena s DPH: {localization.formatPricesArray(prices, 1, true).join(', ')}/m</small>
										</p>
										</>
									}
								/>;

							})
						}
						</div>

						{
							formData.step && <>
								<Spacer initial={30} />

								<h3>Délka schodišťového stupně (šířka schodiště) [mm]:</h3>
								<Input
										label={ 'V rozmezí ' + formData.step.minLength + 'mm - ' + formData.step.maxLength + 'mm' }
										value={inputStepLength}
										min={formData.step.minLength ?? 0}
										max={formData.step.maxLength ?? 10000}
										type={'number'}
										onChange={(e) => numericInputChangeHandler(e, setInputStepLength, (num) => setFormData({...formData, stepLength: num}))}
								/>

								{
									state <= StaircaseAssemblyConfiguratorState.step && 
									<div>
										<Spacer initial={40} />
										<Button onClick={() => setState(StaircaseAssemblyConfiguratorState.stringer)} disabled={!formData.stepLength}>Pokračovat s vybranou délkou</Button>	
									</div>
								}
							</>
						}


						

					</div>
					<StaircasesSVGComponent />	
				</div>
			</div>
		</>
	);
};


/**************************************************************** */
/** Krok 3 - parametry schodnice ******************************** */
/**************************************************************** */

const StaircaseAssemblyConfiguratorStringer : React.FunctionComponent<StaircaseAssemblyConfiguratorStateProps> = 
	({ locales, stringers, state, setState, formData, setFormData }) => {

		const [inputStringerHeight, setInputStringerHeight] = React.useState<string>('');
		const [rounded, setRounded] = React.useState<boolean>(false);
		const [dirty, setDirty] = React.useState<boolean>(false);
		const timeout = React.useRef<ReturnType<typeof setTimeout> | undefined>();

		React.useEffect(() => {
			if(state === StaircaseAssemblyConfiguratorState.stringer)
			{
				setFormData({...formData,
					stringer: null,
					stringerHeight: null,
					stringerCount: null
				});

				setRounded(false);
				setDirty(false);
				setInputStringerHeight('');
			}
		}, [state]);

		const localization = useLocalization();
		const ref = React.useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
		React.useEffect(() => {
			if(ref.current)
			{
				ref.current.scrollIntoView({
					behavior: 'smooth'
				});
			}
		}, [ref]);

		const [minHeight, maxHeight] = React.useMemo(() => {
			return formData.stringer ? [formData.stringer.minHeight, formData.stringer.maxHeightsByStepLengths.find(arr => arr[0] > formData.stepLength!)![1]] : [0, 10000];
		}, [formData]);

		React.useEffect(() => {

			if(formData.stringerHeight)
			{
				const h = Math.min(Math.max(formData.stringerHeight, minHeight), maxHeight);
				if(formData.stringerHeight != h)
				{
					setFormData({
						...formData,
						stringerHeight: h
					});

					setInputStringerHeight(h.toString());
					setDirty(false);
					setRounded(false);
				}
			}
			
		}, [formData, minHeight, maxHeight]);

		React.useEffect(() => {
			if(formData.stringer)
			{
				setDirty(true);

				if(timeout.current)
				{
					clearTimeout(timeout.current);
					timeout.current = undefined;
				}

				timeout.current = setTimeout(() => {
					const heightNum = parseInt(inputStringerHeight);
					if(heightNum && !isNaN(heightNum) && formData.stringer)
					{
						let val = Math.max(formData.stringer.minHeight, Math.min(heightNum, maxHeight! - formData.stringer.spacing + 1));
						val = formData.stringer.spacing * Math.ceil(val / formData.stringer.spacing);
						const count = StaircaseAssemblyConfiguratorUtils.getStaircaseStepCountForHeight(val, formData.stringer.spacing, !!(formData.landing || state < StaircaseAssemblyConfiguratorState.landing));
						if(formData.stringerHeight != val || formData.stepCount != count)
						{
							setFormData({
								...formData,
								stringerHeight: val,
								stepCount: count
							});
						}

						if(inputStringerHeight != val.toString())
						{
							setInputStringerHeight(val.toString());
							setRounded(true);
						}
					}
	
					timeout.current = undefined;
					setDirty(false);
				}, 1000);
			}
		}, [inputStringerHeight]);
		
		return (
			<>
				<Spacer initial={80}/>
				<div ref={ref} className={classNames('staircaseConfiguratorLayout-state', 'staircaseConfiguratorLayout-stringers')}>
					<header>
						<h2><span className="number">3</span> Schodnice a nosník podesty</h2>
						<p>Na schodnici jsou položeny schodišťové stupně a podesta. Výška schodiště udává výšku schodnice a tím i počet potřebných schodišťových stupňů. Maximální výška schodiště se odvíjí od jeho šířky.</p>
					</header>
					<div className="grid--image">	
						<div>

						<h3>Vyberte:</h3>
						<div className="grid--selection">
						{
							stringers && stringers.filter(s => s.depths.includes(formData.step!.depth)).map(st => {
								
								const data = getLocale(st.locales, locales);
								
								let prices = localization.extractPrices(st.pricesFixed);
								prices = StaircaseAssemblyConfiguratorUtils.applyAssemblyPriceMultipliers(prices);

								return data && <CardBox 
									isActive={ formData.stringer == st }
									onClick={() => {
										const h = formData.stringerHeight && maxHeight ? Math.max(st.minHeight, Math.min(formData.stepLength!, maxHeight!)) : null;
										setFormData({
											...formData,
											stringer: st,
											stringerCount: 2,
											stringerHeight: h,
											stepCount: h ? StaircaseAssemblyConfiguratorUtils.getStaircaseStepCountForHeight(h, st.spacing, !!(formData.landing || state < StaircaseAssemblyConfiguratorState.landing)) : null
										});
										setInputStringerHeight(h?.toString() ?? '');
									}}
									title={data.name}
									categoryTag={'Minimální výška ' + st.minHeight  + 'mm'}
									view={CardView.default}
									content={
										<>
										
										<p>
											<small>Cena s DPH od: {localization.formatPricesArray(prices, 1, true).join(', ')}</small>
										</p>
										</>
									}
								/>;

							})
						}
						</div>

						{
							formData.stringer && <>
								<Spacer initial={30}></Spacer>
								<h3>Výška schodiště (určující počet stupňů a výšku schodnice) [mm]:</h3>

								<Input
									label={ formData.stringer ? ('V rozmezí ' + minHeight + 'mm - ' + maxHeight + 'mm' ) : ''}
									value={inputStringerHeight}
									min={minHeight}
									max={maxHeight}
									autoFocus={true}
									type={'number'}
									onChange={(e) => {
										numericInputChangeHandler(e, setInputStringerHeight, (num) => setFormData({...formData, stringerHeight: num}));
										setRounded(false);										
									}}
									/>

								{
									rounded && <small><SvgQuestionMark width={17} height={17} /> Hodnotu jsme automaticky zaokrouhlili na nejbližší možnou výšku. </small>
								}

								{
									state! <= StaircaseAssemblyConfiguratorState.stringer && 
									<div>
										<Spacer initial={40} />
										<Button onClick={() => setState(StaircaseAssemblyConfiguratorState.landing) } disabled={!formData.stringerHeight || dirty || parseInt(inputStringerHeight) != formData.stringerHeight}>Pokračovat s vybranou výškou</Button>	
									</div>
								}
							
							</>
						}

						</div>
						<StaircasesSVGComponent/>
						
					</div>
				</div>
			</>
		);
};



/**************************************************************** */
/** Krok 4 - volba podesty ************************************** */
/**************************************************************** */

const StaircaseAssemblyConfiguratorLanding : React.FunctionComponent<StaircaseAssemblyConfiguratorStateProps> = 
	({ locales, landings, state, setState, formData, setFormData}) => {
		const localization = useLocalization();
		const [inputLandingDepth, setInputLandingDepth] = React.useState<string>('');
		const [inputLandingLength, setInputLandingLength] = React.useState<string>('');

		const [minLen, maxLen] = React.useMemo(() => {
			return formData.landing && formData.stepLength ? [Math.max(formData.landing.minLength, formData.stepLength), formData.landing.maxLength] : [0, 10000];
		}, [formData]);

		const [minDepth, maxDepth] = React.useMemo(() => {
			return formData.step && formData.landing ? [formData.landing.minDepthsByStepDepths.find(arr => arr[0] == formData.step!.depth)![1], formData.landing.maxDepth] : [0, 10000];
		}, [formData]);

		const [initialSelectionDone, setInitialSelectionDone] = React.useState<boolean>(false);
		React.useEffect(() => {
			if(state === StaircaseAssemblyConfiguratorState.landing)
			{
				if(!landings.filter(ld => ld.samplerIds.includes(formData.sampler!.id)).length)
				{
					throw new Error('No landings available to choose from');
				}
				
				setInitialSelectionDone(!!formData.landing);
			}
		}, [state]);

		React.useEffect(() => {

			if(formData.landingLength && formData.landingDepth)
			{
				const l = Math.min(Math.max(formData.landingLength, minLen), maxLen);
				const d = Math.min(Math.max(formData.landingDepth, minDepth), maxDepth);
				if(formData.landingDepth != d || formData.landingLength != l)
				{
					setFormData({
						...formData,
						landingDepth: d,
						landingLength : l
					});

					setInputLandingDepth(d.toString());
					setInputLandingLength(l.toString());
				}
			}
			
		}, [formData, minLen, maxLen, minDepth, maxDepth]);
		
		const ref = React.useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
		React.useEffect(() => {
			if(ref.current)
			{
				ref.current.scrollIntoView({
					behavior: 'smooth'
				});
			}
		}, [ref]);

		return (
			<>
				<Spacer initial={80}/>
				<div ref={ref} className={classNames('staircaseConfiguratorLayout-state', 'staircaseConfiguratorLayout-landings')}>
					<header>
						<h2><span className="number">4</span> Podesta</h2>
						<p>Podesta je horní deska ukončující schodiště, která bývá nejčastěji zakotvena do stěny. Může být i širší, než je šířka schodiště, v tom případě je však třeba její přečnívající část podepřít dodatečnou podpěrou či podpěrami. </p>
					</header>
					<div className="grid--image">
						
					<div>

					<h3>Vyberte:</h3>
						<div className="grid--selection">
						{
							formData.sampler && landings.filter(ld => ld.samplerIds.includes(formData.sampler!.id)).map(ld => {
								
								const data = getLocale(ld.locales, locales);
								let prices = localization.extractPrices(ld.pricesVolume);
								prices = StaircaseAssemblyConfiguratorUtils.applyAssemblyPriceMultipliers(prices);

								return data && <CardBox 
									isActive={ formData.landing == ld && initialSelectionDone }
									onClick={() => {

										setFormData({
											...formData,
											landing: ld,
											landingDepth: null,
											landingLength: formData.stepLength,
											stepCount: StaircaseAssemblyConfiguratorUtils.getStaircaseStepCountForHeight(formData.stringerHeight!, formData.stringer!.spacing, true)
										});
	
										setInitialSelectionDone(true);
										setInputLandingLength(formData.stepLength!.toString());
										setInputLandingDepth('');
										setState(StaircaseAssemblyConfiguratorState.landing);
									}}
									title={'Schodiště zakončené podestou'}
									categoryTag={'Výška ' + ld.height + 'mm, šířku a délku lze zvolit.'}
									view={CardView.default}
									content={
										<>
										
										<p>
											<small>Cena se odvíjí od objemu a provedení podesty.</small>
										</p>
										</>
									}
								/>;

							})
						}
						<CardBox 
								isActive={ formData.landing == null && initialSelectionDone }
								onClick={() => {

									setFormData({
										...formData,
										landing: null,
										landingDepth: null,
										landingLength: formData.stepLength,
										stepCount: StaircaseAssemblyConfiguratorUtils.getStaircaseStepCountForHeight(formData.stringerHeight!, formData.stringer!.spacing, false)
									});

									setInitialSelectionDone(true);
									setState(StaircaseAssemblyConfiguratorState.support);
								}}
								title={'Schodiště bez podesty'}
								categoryTag={'Hloubka stupně ' + formData.step!.depth + 'mm.'}
								view={CardView.default}
								content={
									<>
										<p>
											<small>Použije k zakončení další schodišťovou desku vybrané hloubky.</small>
										</p>
									</>
								}
							/>
						</div>
							
							{
								formData.landing &&
								<>
									<Spacer initial={30} />
									<h3>Délka podesty (může být delší, než délka stupňů) [mm]:</h3>
									<Input
										label={ formData.landing ? ('V rozmezí ' + minLen + 'mm - ' + maxLen + 'mm' ) : ''}
										value={inputLandingLength}
										min={minLen}
										max={maxLen}
										type={'number'}
										onChange={
											(e) => {
												if(formData.landing)
												{
													numericInputChangeHandler(e, setInputLandingLength, (num) => setFormData({...formData, landingLength: num}));
												}	
											}
										}
									/>

									<h3>Hloubka podesty [mm]:</h3>
									<Input
										label={ formData.landing ? ('V rozmezí ' + minDepth + 'mm - ' + maxDepth + 'mm' ) : ''}
										value={inputLandingDepth}
										min={minDepth}
										max={maxDepth}
										autoFocus={true}
										type={'number'}
										onChange={(e) => {
												if(formData.landing)
												{
													numericInputChangeHandler(e, setInputLandingDepth, (num) => setFormData({...formData, landingDepth: num}))	
												}
											}
										}
									/>


									<Spacer initial={30} />

									{
										state! <= StaircaseAssemblyConfiguratorState.landing && 
										<div>
											<Spacer initial={40} />
											<Button onClick={() => setState(StaircaseAssemblyConfiguratorState.support)} disabled={!formData.landingDepth || parseInt(inputLandingDepth) != formData.landingDepth || !formData.landingLength || parseInt(inputLandingLength) != formData.landingLength}>Pokračovat s vybranou podestou</Button>	
										</div>
									} 


								</>
							}
							
						</div>
						<StaircasesSVGComponent/>
					</div>
				</div>
			</>
		);
};


/**************************************************************** */
/** Krok 5 - vyber podper  ************************************** */
/**************************************************************** */

const StaircaseAssemblyConfiguratorSupports : React.FunctionComponent<StaircaseAssemblyConfiguratorStateProps> = 
	({ supports, formData, setFormData, state, setState }) => {
		
	const ref = React.useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
	React.useEffect(() => {
		if(ref.current)
		{
			ref.current.scrollIntoView({
				behavior: 'smooth'
			});
		}
	}, [ref]);

	React.useEffect(() => {
		if(state === StaircaseAssemblyConfiguratorState.support)
		{
			if(!supports.length)
			{
				throw new Error('No supports available to choose from');
			}

			setFormData({...formData,
				support: supports[0]
			});

			setState(StaircaseAssemblyConfiguratorState.final);
		}
	}, [state]);

	React.useEffect(() => {

		const supportCount = formData.support && formData.landingLength && formData.stepLength ? StaircaseAssemblyConfiguratorUtils.determineSupportCount(!!formData.landing, formData.stringer!.maxDepthsByStepLengths, formData.landing?.overhangThreshold ?? 0, formData.landingLength!, formData.landingDepth!, formData.stepCount!, formData.step!.depth, formData.stepLength!) : null;
		if(supportCount != formData.supportCount)
		{
			setFormData({...formData,
				supportCount: supportCount
			});
		}

	}, [formData]);
	
	return (
		<>
			<Spacer initial={80} />
			<div ref={ref} className={classNames('staircaseConfiguratorLayout-state', 'staircaseConfiguratorLayout-supports')}>
				<header>
					<h2><span className="number">5</span> Podpěry</h2>
					<p>Podpěra přenáší zatížení od schodnic v místě schodišťové podesty, jejím nejdůležitějším parametrem je výška. 
						Pro případ, kdy je podesta širší než schodiště o více než je dovolený přesah, budete potřebovat dodatečnou podpěru pro podepření přečnívající části. 
						<strong className='text-primary'> Podpěry standardně vyhotovujeme v hladkém provedení, a to i k tryskaným schodišťovým stupňům. V případě požadavku na tryskání podpěry je nutné počítat s navýšením ceny o přibližně 30% (uveďte prosím do poznámky).</strong></p>
				</header>
				<div className="grid--image">	
					<div>
						
						<Spacer initial={40} />
						<div className="grid--price">
									<table>
										<tbody>
											<tr>
												<td>Počet podpěr dle půdorysné délky a šířky schodiště:</td>
												<td><strong>{ formData.supportCount?.toString() }</strong></td>
											</tr>
										</tbody>
									</table>
								</div>
						</div>
						<StaircasesSVGComponent/>
					</div>
					
				</div>
		</>
	);
}

/**************************************************************** */
/** Krok 6 - zobrazeni vysledku  ******************************** */
/**************************************************************** */

const StaircaseAssemblyConfiguratorFinal : React.FunctionComponent<StaircaseAssemblyConfiguratorStateProps> = 
	({ locales, formData, state }) => {

	const localization = useLocalization();

	let link = '';
	if(globalThis.window && state === StaircaseAssemblyConfiguratorState.final)
	{
		const dto = {
			...formData,
			samplerId: formData.sampler!.id,
			stepId: formData.step!.id,
			stringerId: formData.stringer!.id,
			landingId: formData.landing?.id ?? null,
			supportId: formData.support!.id,
			sampler: undefined,
			step: undefined,
			stringer: undefined,
			landing: undefined, 
			support: undefined
		} as StaircaseAssemblyConfiguratorDTO;

		link = window.location.toString().replace(/\?.*$/g, '') + '?share=' + 
		encodeURIComponent(Buffer.from(JSON.stringify(dto), 'utf8').toString('base64')) + '#tab-konfigurator';
	}

	const hasLanding = React.useMemo(() => {
		return !!(formData.landing || state < StaircaseAssemblyConfiguratorState.landing);
	}, [formData, state]);

	const stepPrices = React.useMemo(() => {
		return formData.step && formData.stepLength ? StaircaseAssemblyConfiguratorUtils.getStaircaseStepPrice(localization, formData.step.prices, formData.stepLength, formData.step.depth) : null;
	}, [formData]);

	const stepPricesSum = React.useMemo(() => {
		return stepPrices && formData.stepCount ? multiplyPrices(stepPrices, formData.stepCount) : null;
	}, [stepPrices, formData]);

	const stringerPricesWithoutHorizontal = React.useMemo(() => {
		return formData.stringer && formData.stringerHeight && formData.stepCount ? StaircaseAssemblyConfiguratorUtils.getStaircaseStringerPricesWithoutHorizontal(localization, hasLanding, formData.stringer.pricesFixed, formData.stringer.pricesStep, formData.stepCount) : null;
	}, [formData, hasLanding]);	

	const stringerPrices = React.useMemo(() => {
		return formData.stringer && formData.stringerHeight && formData.stepCount ? StaircaseAssemblyConfiguratorUtils.getStaircaseStringerPrices(localization, hasLanding, formData.stringer.pricesFixed, formData.stringer.pricesStep, formData.stringer.pricesAlt, formData.stringer.pricesAltMin, formData.stepCount, formData.landingDepth ?? 0) : null;
	}, [formData, hasLanding]);	 

	const stringerPricesSum = React.useMemo(() => {
		return stringerPrices && formData.stringerCount ? multiplyPrices(stringerPrices, formData.stringerCount) : null;
	}, [stringerPrices, formData]);

	const landingPrices = React.useMemo(() => {
		return formData.landingDepth && formData.landingLength && hasLanding ? StaircaseAssemblyConfiguratorUtils.getStaircaseLandingPrices(localization, formData.landing!.pricesVolume, formData.landing!.pricesLength, formData.landingLength, formData.landingDepth, formData.landing!.height) : null;
	}, [formData, hasLanding]);

	const landingPricesSum = React.useMemo(() => {
		return landingPrices ? multiplyPrices(landingPrices, 1) : null;
	}, [landingPrices]); 

	const supportPrices = React.useMemo(() => {
		return formData.support && formData.stepCount && formData.stepLength ? StaircaseAssemblyConfiguratorUtils.getStaircaseSupportPrices(localization, hasLanding, formData.support, formData.stepCount, formData.stepLength) : null;
	}, [formData, hasLanding]);

	const supportPricesSum = React.useMemo(() => {
		return supportPrices && formData.supportCount ? multiplyPrices(supportPrices, formData.supportCount) : null;;
	}, [supportPrices, formData]); 
	
	const total = React.useMemo(() => {
		return supportPricesSum ? sumPriceArrays([stepPricesSum ?? [], stringerPricesSum ?? [], landingPricesSum ?? [], supportPricesSum ?? []]) : null;
	}, [supportPricesSum, stepPricesSum, stringerPricesSum, landingPricesSum]);

	if(!formData.step || !formData.sampler)
	{
		return <></>;
	}

	const makeTable = () => <>
		<h3>Váš výběr a <span className="text-primary">orientační</span> kalkulace projektu montovaného schodiště:</h3>
		<table>
			<thead>
				<tr>
					<th></th>
					<th>bez DPH</th>
					<th>vč. DPH</th>
				</tr>
			</thead>
			<tbody>
				{
					formData.sampler && formData.step && <tr className='main'>
						<th>
							Schodišťové stupně: 
							{formData.stepCount && <>&nbsp;{ formData.stepCount }ks</>} 
							&nbsp;{ getLocale(formData.sampler.locales, locales)?.name }
							&nbsp;({formData.stepLength && state > StaircaseAssemblyConfiguratorState.step && <>šířka {formData.stepLength}mm, </>}hloubka {formData.step.depth}mm)
						</th>
						<td></td>
						<td></td>
					</tr>
				}
				{
					stepPrices && state > StaircaseAssemblyConfiguratorState.step && <tr>
						<td>Cena: </td>
						<td>{ localization.formatPricesArray(stepPrices).join(', ') }</td>
						<td>{ localization.formatPricesArray(stepPrices, 1, true).join(', ') }</td>
					</tr>
				}
				{
					stepPricesSum && <tr>
						<td>Za stupně celkem: </td>
						<td><strong>{ localization.formatPricesArray(stepPricesSum).join(', ') }</strong></td>
						<td><strong>{ localization.formatPricesArray(stepPricesSum, 1, true).join(', ') }</strong></td>
					</tr>
				}
				{
					formData.stringer && <tr className='main'>
						<th>Schodnice a nosník podesty: 
							{formData.stringerCount && <>&nbsp;{ formData.stringerCount }ks</>}
							&nbsp;{ getLocale(formData.stringer.locales, locales)?.name }
							{formData.stringerHeight && formData.stepCount && <><br/>výška {formData.stringerHeight}mm / {formData.stepCount! + (hasLanding ? 1 : 0)} stupňů</>}
							{formData.landingDepth && <>&nbsp;(s nosníkem podesty {formData.landingDepth - 50}mm)</>}
						</th>
						<td></td>
						<td></td>
					</tr>
				}
				{
					stringerPricesWithoutHorizontal && <tr>
						<td>Cena (bez nosníku podesty):</td>
						<td>{ localization.formatPricesArray(stringerPricesWithoutHorizontal).join(', ') }</td>
						<td>{ localization.formatPricesArray(stringerPricesWithoutHorizontal, 1, true).join(', ') }</td>
					</tr>
				}
				{
					stringerPrices && <tr>
						<td>Cena (vč. nosníku podesty nebo výřezu):</td>
						<td>{ localization.formatPricesArray(stringerPrices).join(', ') }</td>
						<td>{ localization.formatPricesArray(stringerPrices, 1, true).join(', ') }</td>
					</tr>
				}
				{
					stringerPricesSum && <tr>
						<td>Za schodnice a nosníky celkem:</td>
						<td><strong>{ localization.formatPricesArray(stringerPricesSum).join(', ') }</strong></td>
						<td><strong>{ localization.formatPricesArray(stringerPricesSum, 1, true).join(', ') }</strong></td>
					</tr>
				}
				{
					formData.landing && formData.sampler && <tr className='main'>
						<th>Podesta: 1ks { getLocale(formData.sampler.locales, locales)?.name }&nbsp;
						{formData.landingLength && formData.landingDepth && state > StaircaseAssemblyConfiguratorState.landing && <>(šířka { formData.landingLength }mm, hloubka { formData.landingDepth }mm)</>}
						</th>
						<td></td>
						<td></td>
					</tr>
				}
				{
					landingPricesSum && <tr>
						<td>Za podestu celkem:</td>
						<td><strong>{ localization.formatPricesArray(landingPricesSum).join(', ') }</strong></td>
						<td><strong>{ localization.formatPricesArray(landingPricesSum, 1, true).join(', ') }</strong></td>
					</tr>
				}
				{
					formData.support && <tr className='main'>
						<th>Podpěra Hladká:
							{formData.supportCount && <>&nbsp;{ formData.supportCount }ks</>}&nbsp;
						</th>
						<td></td>
						<td></td>
					</tr>
				}
				{
					supportPrices && state > StaircaseAssemblyConfiguratorState.step && <tr>
						<td>Cena: </td>
						<td>{ localization.formatPricesArray(supportPrices).join(', ') }</td>
						<td>{ localization.formatPricesArray(supportPrices, 1, true).join(', ') }</td>
					</tr>
				}
				{
					supportPricesSum && <tr>
						<td>Za podpěry celkem: </td>
						<td><strong>{ localization.formatPricesArray(supportPricesSum).join(', ') }</strong></td>
						<td><strong>{ localization.formatPricesArray(supportPricesSum, 1, true).join(', ') }</strong></td>
					</tr>
				}
			</tbody>
			<tfoot>
				{ total && 
					<tr>
						<th><strong>CELKEM: </strong></th>
						<td><strong>{ localization.formatPricesArray(total, undefined, false).join(', ') }</strong></td>
						<td><strong>{ localization.formatPricesArray(total, 1, true).join(', ') }</strong></td>
					</tr>
				}
			</tfoot>
		</table>
	</>;

	return <>
		<Spacer initial={50} />
		<div className={classNames('staircaseConfiguratorLayout-state', 'staircaseConfiguratorLayout-final')}>
			<header>
				<h2>Shrnutí a poptávka</h2>
			</header>
			<div className="grid--price">
				{ makeTable() }
			</div>
			{
				total && <>
					<div className="grid--image">
						<div>
							<div className="staircaseConfiguratorLayout--share">
								<ShareLink link={link} />
								<ExportButton 
									filename='presbeton_montovana_schodiste' 
									landscape={true} 
									documentClassName='staircaseConfiguratorLayout-export'
								>{ localization.translate('print') }</ExportButton>
							</div>

							<h3>Poptejte u nás svůj projekt</h3>
							<ContactMessageDetailsContextProvider type='configurator' subject="PRESBETON - Konfigurátor montovaných schodišť" emailContentCallback={() => 
								ReactDOMServer.renderToString(<>
									<h2>Projekt konfigurátoru montovaných schodišť</h2>

									<div>
										<img src={formData.sampler!.image.previewUrl} width="50" height="50" style={{marginRight: '10px', marginBottom: '10px'}}/>	
										{ getLocale(formData.sampler!.locales, locales)?.name }						
									</div>

									{ makeTable() }
								</>)
							}
							>
								<ContactForm
									formAdminEmail="marek.mastny@presbeton.cz"
									note={
										'Cenovou kalkulaci dostanete v kopii emailem. Své poznámky nám prosíme doplňte dole do zprávy formuláře.'
									}
									requireCity={true}
									requireFormFields={true}
								/>
							</ContactMessageDetailsContextProvider>
						</div>
					<StaircasesSVGComponent/>
			</div>
				</>
			}
		</div> 
	</>
};

export interface StaircaseAssemblyConfiguratorLayoutProps {
	locales: Locale[],
	steps: StaircaseAssemblyConfiguratorStepFragment[],
	stringers: StaircaseAssemblyConfiguratorStringerFragment[],
	landings: StaircaseAssemblyConfiguratorLandingFragment[],
	supports: StaircaseAssemblyConfiguratorSupportFragment[],
	samplers: StaircaseAssemblyConfiguratorSamplerFragment[],
	priceCzkMultiplier: number,
	priceEurSkMultiplier: number
}

export const StaircaseAssemblyConfiguratorLayout: React.FunctionComponent<StaircaseAssemblyConfiguratorLayoutProps> =
	({ locales, steps, stringers, landings, supports, samplers, priceCzkMultiplier, priceEurSkMultiplier }) => {

		StaircaseAssemblyConfiguratorUtils.setAssemblyPriceMultipliers(priceCzkMultiplier, priceEurSkMultiplier);


		const [formData, setFormData] = React.useState<StaircaseAssemblyConfiguratorFormData | null>(null);
		const [state, setState] = React.useState<StaircaseAssemblyConfiguratorState>(StaircaseAssemblyConfiguratorState.surface);

		React.useEffect(() => {

			if(!formData)
			{
				setFormData(createBlankFormData());
			}

			if(window)
			{
				const param = Object.fromEntries(window.location.search.replace('?', '').split('&').map(str => str.split('=')));
				if(param['share'] !== undefined)
				{
					const buffer = Buffer.from(decodeURIComponent(param['share']), 'base64');
					const json = JSON.parse(buffer.toString('utf8'));

					if(Object.keys(json).filter(v => ['samplerId', 'stepId', 'stringerId', 'landingId', 'supportId'].includes(v)).length == 5)
					{
						const dto = json as StaircaseAssemblyConfiguratorDTO;
						const step = steps.find(st => st.id == dto.stepId);
						const stringer = stringers.find(st => st.id == dto.stringerId);
						const landing = landings.find(l => l.id == dto.landingId) ?? null;
						const support = supports.find(s => s.id == dto.supportId);
						const sampler = samplers.find(ss => ss.id == dto.samplerId);

						if(sampler && step && stringer && (landing || dto.landingId === null) && support)
						{
							setFormData({
								...dto,
								sampler, step, stringer, support, landing
							});

							setState(StaircaseAssemblyConfiguratorState.final);
						}
					}
				}
			}
		}, []);

		if(!formData)
		{
			return <></>;
		}

		const stateProps = {
			formData, setFormData : (data: StaircaseAssemblyConfiguratorFormData) => {setFormData(data);}, state, setState, samplers, steps, stringers, landings, supports, locales
		};

		return (
				<div className="staircaseConfiguratorLayout">
						<div>
							<h2>Konfigurátor montovaných schodišť</h2>
							<p>Svůj projekt i jeho poptávku vyřešíte jednoduše v tomto konfigurátoru. Vyberte z nabídky dostupných schodišťových prvků, zadejte požadované rozměry, a výsledný projekt si budete moci vytisknout do PDF nebo jej odeslat rovnou našim specialistům za pomoci k tomu určeného formuláře.</p>

							<p>Každé montované schodiště se skládá ze 4 základních prvků:</p>
							<ol>
								<li><strong>Schodišťových stupňů	</strong></li>
								<li><strong>Schodnice</strong> - lišty, do které se zasazují jednotlivé stupně</li>
								<li><strong>Podesty</strong> - vrchního zakončení schodiště</li>
								<li><strong>Podpěr</strong> - pro konstrukční upevnění podesty do požadované výšky</li>
							</ol>

							<Spacer initial={30} />
							<p>Jednotlivé prvky dodáváme jako pohledový beton v kombinaci s pemrlováním, nebo jako beton tryskaný. Standardně se prvky montovaného schodiště vyrábí v přírodním barevném provedení. Na objednávku lze prvky dodat také v barevném provedení cihlová, okrová  nebo písková.</p>
						
							<div className="staircaseConfiguratorLayout-thumbs">
								{
									samplers.map(sampler => {

										const samplerLocale = getLocale(sampler.locales, locales);
										
										return samplerLocale && <>
											<div>
												<Picture
												image={sampler.image}
												width={140}
												height={140}
												mode="cover"
												decoding='sync'
												preload={true}
												encode={PictureEncode.jpg}
												alt={samplerLocale.name}
												/>
												<p dangerouslySetInnerHTML={{ __html: samplerLocale.name }}></p>
											</div>
										</>

									} )
								}
							</div>
						
						</div>
						<div className="staircaseConfiguratorLayout-inner">
							{ formData && <>
								<Spacer initial={80} />

								    <StaircaseAssemblyConfiguratorSurfaceFinish
										{...stateProps}
									/>

								    { state >= StaircaseAssemblyConfiguratorState.step &&<StaircaseAssemblyConfiguratorStep
										{...stateProps}
									/>}

									{ state >= StaircaseAssemblyConfiguratorState.stringer && <StaircaseAssemblyConfiguratorStringer 
										{...stateProps}
										/>
									}

									{ state >= StaircaseAssemblyConfiguratorState.landing && <StaircaseAssemblyConfiguratorLanding 
										{...stateProps}
										/>
									}

									{ state >= StaircaseAssemblyConfiguratorState.support && <StaircaseAssemblyConfiguratorSupports 
										{...stateProps}
										/>
									}

									<StaircaseAssemblyConfiguratorFinal
										{...stateProps}
									/>

								<Spacer initial={200} />
							</>}
						</div>

				</div>
			
		);
	}


const StaircasesSVGComponent: React.FunctionComponent = ({}) => 
{
	return <>
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 841.98 757.68" preserveAspectRatio="xMidYMid meet" className="staircaseSVG">
	  <g id="podpera">
		<polygon className="cls-2" points="169.36 276.07 182.15 265.04 182.47 412.6 169.67 423.63 169.36 276.07"/>
		<polygon className="cls-4" points="169.36 276.07 182.15 265.04 182.47 412.6 169.67 423.63 169.36 276.07"/>
		<polygon className="cls-2" points="169.36 276.07 169.67 423.63 157.86 415.37 157.54 267.79 169.36 276.07"/>
		<polygon className="cls-4" points="169.36 276.07 169.67 423.63 157.86 415.37 157.54 267.79 169.36 276.07"/>
		<polygon className="cls-2" points="283.92 175.02 296.71 164.01 297.03 311.58 284.23 322.6 283.92 175.02"/>
		<polygon className="cls-4" points="283.92 175.02 296.71 164.01 297.03 311.58 284.23 322.6 283.92 175.02"/>
		<polygon className="cls-2" points="283.92 175.02 284.23 322.6 272.41 314.32 272.11 166.75 283.92 175.02"/>
		<polygon className="cls-4" points="283.92 175.02 284.23 322.6 272.41 314.32 272.11 166.75 283.92 175.02"/>
		<polygon className="cls-2" points="149.17 277.7 309.06 139.92 309.09 155.71 149.2 293.49 149.17 277.7"/>
		<polygon className="cls-4" points="149.17 277.7 309.06 139.92 309.09 155.71 149.2 293.49 149.17 277.7"/>
		<polygon className="cls-3" points="137.37 269.43 297.26 131.67 309.06 139.92 149.16 277.7 137.37 269.43"/>
		<polygon className="cls-4" points="137.37 269.43 297.26 131.67 309.06 139.92 149.16 277.7 137.37 269.43"/>
		<polygon className="cls-2" points="149.17 277.7 149.2 293.49 137.4 285.22 137.37 269.44 149.17 277.7"/>
		<polygon className="cls-4" points="149.17 277.7 149.2 293.49 137.4 285.22 137.37 269.44 149.17 277.7"/>
	  </g>
	  <g id="schodnice">
		<polygon className="cls-3" points="157.54 267.78 170.34 256.77 182.16 265.04 169.37 276.07 157.54 267.78"/>
		<polygon className="cls-4" points="157.54 267.78 170.34 256.77 182.16 265.04 169.37 276.07 157.54 267.78"/>
		<polygon className="cls-2" points="139.08 265.26 155.11 251.45 155.17 257.02 139.15 270.84 139.08 265.26"/>
		<polygon className="cls-4" points="139.08 265.26 155.11 251.45 155.17 257.02 139.15 270.84 139.08 265.26"/>
		<polygon className="cls-2" points="588.64 696.3 604.67 682.48 604.77 691.36 588.74 705.18 588.64 696.3"/>
		<polygon className="cls-4" points="588.64 696.3 604.67 682.48 604.77 691.36 588.74 705.18 588.64 696.3"/>
		<polygon className="cls-1" points="560.1 644.46 576.13 630.63 626.97 666.34 610.93 680.17 560.1 644.46"/>
		<polygon className="cls-4" points="560.1 644.46 576.13 630.63 626.97 666.34 610.93 680.17 560.1 644.46"/>
		<polygon className="cls-3" points="610.93 680.16 626.97 666.34 627.08 703.27 610.76 717.38 610.93 680.16"/>
		<polygon className="cls-4" points="610.93 680.16 626.97 666.34 627.08 703.27 610.76 717.38 610.93 680.16"/>
		<polygon className="cls-1" points="508.27 581.71 524.31 567.88 575.81 604.11 559.78 617.94 508.27 581.71"/>
		<polygon className="cls-4" points="508.27 581.71 524.31 567.88 575.81 604.11 559.78 617.94 508.27 581.71"/>
		<polygon className="cls-3" points="559.78 617.93 575.82 604.12 576.13 630.63 560.1 644.46 559.78 617.93"/>
		<polygon className="cls-4" points="559.78 617.93 575.82 604.12 576.13 630.63 560.1 644.46 559.78 617.93"/>
		<polygon className="cls-1" points="457.8 520.09 473.84 506.28 524.1 541.29 508.07 555.12 457.8 520.09"/>
		<polygon className="cls-4" points="457.8 520.09 473.84 506.28 524.1 541.29 508.07 555.12 457.8 520.09"/>
		<polygon className="cls-3" points="508.07 555.12 524.1 541.29 524.31 567.88 508.27 581.71 508.07 555.12"/>
		<polygon className="cls-4" points="508.07 555.12 524.1 541.29 524.31 567.88 508.27 581.71 508.07 555.12"/>
		<polygon className="cls-1" points="407 458.76 423.04 444.94 473.73 480.43 457.7 494.25 407 458.76"/>
		<polygon className="cls-4" points="407 458.76 423.04 444.94 473.73 480.43 457.7 494.25 407 458.76"/>
		<polygon className="cls-3" points="457.7 494.25 473.73 480.43 473.84 506.27 457.8 520.1 457.7 494.25"/>
		<polygon className="cls-4" points="457.7 494.25 473.73 480.43 473.84 506.27 457.8 520.1 457.7 494.25"/>
		<polygon className="cls-2" points="406.76 432.74 407 458.76 457.7 494.25 457.8 520.1 508.07 555.12 508.27 581.71 559.79 617.94 560.1 644.46 610.94 680.16 610.76 717.37 601.52 710.81 601.54 705.08 588.64 696.31 588.74 705.18 357.58 423.13 154.49 281.54 154.43 276.16 139.08 265.26 139.15 270.84 88.99 236.47 88.77 210.74 406.76 432.74"/>
		<polygon className="cls-4" points="406.76 432.74 407 458.76 457.7 494.25 457.8 520.1 508.07 555.12 508.27 581.71 559.79 617.94 560.1 644.46 610.94 680.16 610.76 717.37 601.52 710.81 601.54 705.08 588.64 696.31 588.74 705.18 357.58 423.13 154.49 281.54 154.43 276.16 139.08 265.26 139.15 270.84 88.99 236.47 88.77 210.74 406.76 432.74"/>
		<polygon className="cls-3" points="406.76 432.74 422.79 418.92 423.04 444.94 407 458.76 406.76 432.74"/>
		<polygon className="cls-4" points="406.76 432.74 422.79 418.92 423.04 444.94 407 458.76 406.76 432.74"/>
		<polygon className="cls-1" points="88.76 210.74 104.8 196.92 422.79 418.92 406.76 432.74 88.76 210.74"/>
		<polygon className="cls-4" points="88.76 210.74 104.8 196.92 422.79 418.92 406.76 432.74 88.76 210.74"/>
		<polygon className="cls-2" points="281.19 142.45 297.22 128.62 297.28 134.21 281.26 148.03 281.19 142.45"/>
		<polygon className="cls-4" points="281.19 142.45 297.22 128.62 297.28 134.21 281.26 148.03 281.19 142.45"/>
		<polygon className="cls-2" points="730.74 573.5 746.77 559.68 746.88 568.56 730.85 582.36 730.74 573.5"/>
		<polygon className="cls-4" points="730.74 573.5 746.77 559.68 746.88 568.56 730.85 582.36 730.74 573.5"/>
		<polygon className="cls-1" points="702.21 521.64 718.24 507.82 769.06 543.52 753.03 557.35 702.21 521.64"/>
		<polygon className="cls-4" points="702.21 521.64 718.24 507.82 769.06 543.52 753.03 557.35 702.21 521.64"/>
		<polygon className="cls-3" points="753.03 557.35 769.06 543.52 769.19 580.44 752.87 594.57 753.03 557.35"/>
		<polygon className="cls-4" points="753.03 557.35 769.06 543.52 769.19 580.44 752.87 594.57 753.03 557.35"/>
		<polygon className="cls-1" points="650.38 458.89 666.41 445.08 717.92 481.3 701.89 495.12 650.38 458.89"/>
		<polygon className="cls-4" points="650.38 458.89 666.41 445.08 717.92 481.3 701.89 495.12 650.38 458.89"/>
		<polygon className="cls-3" points="701.89 495.12 717.92 481.3 718.23 507.82 702.21 521.64 701.89 495.12"/>
		<polygon className="cls-4" points="701.89 495.12 717.92 481.3 718.23 507.82 702.21 521.64 701.89 495.12"/>
		<polygon className="cls-1" points="599.9 397.29 615.93 383.47 666.21 418.48 650.18 432.31 599.9 397.29"/>
		<polygon className="cls-4" points="599.9 397.29 615.93 383.47 666.21 418.48 650.18 432.31 599.9 397.29"/>
		<polygon className="cls-3" points="650.18 432.31 666.21 418.48 666.41 445.07 650.38 458.9 650.18 432.31"/>
		<polygon className="cls-4" points="650.18 432.31 666.21 418.48 666.41 445.07 650.38 458.9 650.18 432.31"/>
		<polygon className="cls-1" points="549.11 335.94 565.14 322.12 615.84 357.62 599.81 371.45 549.11 335.94"/>
		<polygon className="cls-4" points="549.11 335.94 565.14 322.12 615.84 357.62 599.81 371.45 549.11 335.94"/>
		<polygon className="cls-3" points="599.81 371.44 615.84 357.62 615.93 383.46 599.91 397.29 599.81 371.44"/>
		<polygon className="cls-4" points="599.81 371.44 615.84 357.62 615.93 383.46 599.91 397.29 599.81 371.44"/>
		<polygon className="cls-2" points="548.86 309.94 549.11 335.94 599.81 371.45 599.9 397.29 650.18 432.31 650.38 458.9 701.9 495.12 702.21 521.64 753.03 557.35 752.87 594.56 743.62 587.99 743.65 582.27 730.74 573.5 730.85 582.36 499.68 300.32 296.6 158.73 296.53 153.35 281.19 142.45 281.25 148.03 231.09 113.65 230.87 87.92 548.86 309.94"/>
		<polygon className="cls-4" points="548.86 309.94 549.11 335.94 599.81 371.45 599.9 397.29 650.18 432.31 650.38 458.9 701.9 495.12 702.21 521.64 753.03 557.35 752.87 594.56 743.62 587.99 743.65 582.27 730.74 573.5 730.85 582.36 499.68 300.32 296.6 158.73 296.53 153.35 281.19 142.45 281.25 148.03 231.09 113.65 230.87 87.92 548.86 309.94"/>
		<polygon className="cls-3" points="548.86 309.94 564.9 296.11 565.14 322.12 549.11 335.94 548.86 309.94"/>
		<polygon className="cls-4" points="548.86 309.94 564.9 296.11 565.14 322.12 549.11 335.94 548.86 309.94"/>
		<polygon className="cls-1" points="230.87 87.92 246.9 74.1 564.89 296.11 548.86 309.94 230.87 87.92"/>
		<polygon className="cls-4" points="230.87 87.92 246.9 74.1 564.89 296.11 548.86 309.94 230.87 87.92"/>
	  </g>
	  <g id="stupen1">
		<polygon className="cls-2" points="521.58 741.31 521.62 757.1 461.37 714.92 461.34 699.14 521.58 741.31"/>
		<polygon className="cls-3" points="521.58 741.31 841.36 465.76 841.39 481.55 521.62 757.1 521.58 741.31"/>
		<polygon className="cls-1" points="461.34 699.13 781.12 423.58 841.36 465.76 521.59 741.31 461.34 699.13"/>
		<g>
		  <path className="cls-2" d="m822.38,462.83c2.98,1.86,3.04,5.42.12,7.94l-293.86,253.51c-2.92,2.51-7.86,2.83-10.98.7-3.12-2.12-3.29-5.91-.36-8.42l294.35-252.54c2.93-2.5,7.76-3.05,10.73-1.2"/>
		  <path className="cls-4" d="m822.38,462.83c2.98,1.86,3.04,5.42.12,7.94l-293.86,253.51c-2.92,2.51-7.86,2.83-10.98.7-3.12-2.12-3.29-5.91-.36-8.42l294.35-252.54c2.93-2.5,7.76-3.05,10.73-1.2Z"/>
		</g>
		<polygon className="cls-4" points="521.58 741.31 841.36 465.76 841.39 481.55 521.62 757.1 521.58 741.31"/>
		<polygon className="cls-4" points="461.34 699.13 781.12 423.58 841.36 465.76 521.59 741.31 461.34 699.13"/>
		<polygon className="cls-4" points="521.58 741.31 521.62 757.1 461.37 714.92 461.34 699.14 521.58 741.31"/>
	  </g>
	  <g id="stupen2">
		<polygon className="cls-3" points="471.56 681.28 791.34 405.73 791.37 421.52 471.58 697.08 471.56 681.28"/>
		<polygon className="cls-1" points="411.31 639.1 731.09 363.55 791.34 405.72 471.56 681.28 411.31 639.1"/>
		<g>
		  <path className="cls-2" d="m772.35,402.81c2.97,1.84,3.03,5.4.12,7.92l-293.86,253.52c-2.92,2.53-7.86,2.84-10.98.7-3.12-2.11-3.29-5.9-.37-8.41l294.36-252.54c2.93-2.5,7.76-3.05,10.74-1.19"/>
		  <path className="cls-4" d="m772.35,402.81c2.97,1.84,3.03,5.4.12,7.92l-293.86,253.52c-2.92,2.53-7.86,2.84-10.98.7-3.12-2.11-3.29-5.9-.37-8.41l294.36-252.54c2.93-2.5,7.76-3.05,10.74-1.19Z"/>
		</g>
		<polygon className="cls-4" points="471.56 681.28 791.34 405.73 791.37 421.52 471.58 697.08 471.56 681.28"/>
		<polygon className="cls-4" points="411.31 639.1 731.09 363.55 791.34 405.72 471.56 681.28 411.31 639.1"/>
		<polygon className="cls-2" points="471.56 681.28 471.58 697.08 411.34 654.89 411.31 639.11 471.56 681.28"/>
		<polygon className="cls-4" points="471.56 681.28 471.58 697.08 411.34 654.89 411.31 639.11 471.56 681.28"/>
	  </g>
	  <g id="stupen3">
		<polygon className="cls-2" points="421.15 620.98 421.19 636.77 360.94 594.58 360.91 578.8 421.15 620.98"/>
		<polygon className="cls-3" points="421.15 620.98 740.93 345.42 740.96 361.21 421.19 636.77 421.15 620.98"/>
		<polygon className="cls-1" points="360.91 578.8 680.7 303.24 740.93 345.42 421.15 620.98 360.91 578.8"/>
		<polygon className="cls-4" points="421.15 620.98 421.19 636.77 360.94 594.58 360.91 578.8 421.15 620.98"/>
		<g>
		  <path className="cls-2" d="m721.95,342.5c2.98,1.86,3.03,5.42.12,7.94l-293.87,253.51c-2.92,2.51-7.85,2.83-10.98.7-3.11-2.12-3.28-5.91-.36-8.41l294.36-252.54c2.92-2.5,7.75-3.05,10.72-1.2"/>
		  <path className="cls-4" d="m721.95,342.5c2.98,1.86,3.03,5.42.12,7.94l-293.87,253.51c-2.92,2.51-7.85,2.83-10.98.7-3.11-2.12-3.28-5.91-.36-8.41l294.36-252.54c2.92-2.5,7.75-3.05,10.72-1.2Z"/>
		</g>
		<polygon className="cls-4" points="421.15 620.98 740.93 345.42 740.96 361.21 421.19 636.77 421.15 620.98"/>
		<polygon className="cls-4" points="360.91 578.8 680.7 303.24 740.93 345.42 421.15 620.98 360.91 578.8"/>
	  </g>
	  <g id="stupen4">
		<polygon className="cls-3" points="369.49 559.58 689.28 284.03 689.31 299.82 369.52 575.37 369.49 559.58"/>
		<polygon className="cls-1" points="309.25 517.41 629.02 241.85 689.28 284.03 369.49 559.58 309.25 517.41"/>
		<polygon className="cls-2" points="369.49 559.58 369.52 575.37 309.28 533.19 309.25 517.41 369.49 559.58"/>
		<g>
		  <path className="cls-2" d="m670.29,281.11c2.98,1.85,3.02,5.42.12,7.93l-293.86,253.52c-2.92,2.5-7.85,2.82-10.98.7-3.12-2.12-3.28-5.91-.36-8.41l294.36-252.54c2.92-2.5,7.75-3.05,10.73-1.2"/>
		  <path className="cls-4" d="m670.29,281.11c2.98,1.85,3.02,5.42.12,7.93l-293.86,253.52c-2.92,2.5-7.85,2.82-10.98.7-3.12-2.12-3.28-5.91-.36-8.41l294.36-252.54c2.92-2.5,7.75-3.05,10.73-1.2Z"/>
		</g>
		<polygon className="cls-4" points="309.25 517.41 629.02 241.85 689.28 284.03 369.49 559.58 309.25 517.41"/>
		<polygon className="cls-4" points="369.49 559.58 369.52 575.37 309.28 533.19 309.25 517.41 369.49 559.58"/>
		<polygon className="cls-4" points="369.49 559.58 689.28 284.03 689.31 299.82 369.52 575.37 369.49 559.58"/>
	  </g>
	  <g id="podesta">
		<polygon className="cls-2" points=".62 291.94 315.95 512.77 315.92 496.99 .59 276.15 .62 291.94"/>
		<polygon className="cls-3" points="315.92 496.99 635.71 221.43 635.73 237.2 315.95 512.77 315.92 496.99"/>
		<polygon className="cls-1" points=".59 276.15 315.92 496.99 635.71 221.43 320.37 .58 .59 276.15"/>
		<g>
		  <polygon className="cls-4" points="315.92 496.99 315.95 512.77 .62 291.94 .58 276.15 315.92 496.99"/>
		  <path className="cls-2" d="m616.72,218.5c2.98,1.86,3.04,5.42.13,7.94l-293.87,253.51c-2.92,2.51-7.85,2.83-10.98.7-3.12-2.12-3.28-5.91-.36-8.41l294.36-252.54c2.92-2.51,7.75-3.05,10.72-1.2"/>
		  <path className="cls-4" d="m616.72,218.5c2.98,1.86,3.04,5.42.13,7.94l-293.87,253.51c-2.92,2.51-7.85,2.83-10.98.7-3.12-2.12-3.28-5.91-.36-8.41l294.36-252.54c2.92-2.51,7.75-3.05,10.72-1.2Z"/>
		</g>
		<polygon className="cls-4" points="315.92 496.99 635.71 221.43 635.73 237.2 315.95 512.77 315.92 496.99"/>
		<polygon className="cls-4" points=".58 276.15 320.37 .58 635.71 221.43 315.93 496.99 .58 276.15"/>
	  </g>
	</svg>
	</>
}